import React, { Fragment, useState, useEffect, useRef } from 'react'
import { Dropdown, Image, OverlayTrigger, Tooltip, Badge, Button } from 'react-bootstrap'
import ReactTimeAgo from 'react-time-ago'
import { gql, useMutation } from '@apollo/client'
import { ToastContainer } from 'react-toastify'
import Card from '../common/Card'
import CustomToggle from '../../layouts/components/CustomToggle'
import Comments from './Comments'
import Sentiment from '../partials/Sentiment'
import { logos } from '../../utils/platform-logos'
import { showNotification, askConfirmation } from '../../utils/notifications'
import Loader from '../../layouts/components/Loader'
import _ from 'lodash'
import { TrashIcon, VerticalDotsIcon } from '../icons'

const DELETE_FEED = gql`
    mutation DeleteFeed($feedId: ID!) {
        deleteFeed(feedId: $feedId) {
            id
        }
    }
`

const FeedCard = ({ data, platform, index, onHeightChange, onDelete, onDeleting }) => {
    const childRef = useRef()
    const [profileImage, setProfileImage] = useState(data.profile.imageUrl)
    const media = JSON.parse(data.media)
    const keywords = _.map(data.keywords, (keyword) => keyword.value)

    const [deleteFeed, mutation] = useMutation(DELETE_FEED, {
        onError({ graphQLErrors }) {
            showNotification('Error: ' + graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            onDelete(data.id)
        },
    })

    const processDelete = () => {
        deleteFeed({
            variables: {
                feedId: data.id,
            },
        })
    }

    const deleteConfirmation = () => {
        askConfirmation(`You are going to delete the Feed?`, 'Delete', processDelete)
    }

    const openLink = (url) => {
        if (url) {
            window.open(url, '_blank', 'noreferrer')
        }
    }

    useEffect(() => {
        const childHeight = childRef.current.clientHeight
        onHeightChange(index, childHeight)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childRef.current?.clientHeight])

    return (
        <Fragment>
            <div ref={childRef} className="mb-0">
                {mutation.loading || onDeleting === data.id ? (
                    <Loader fullScreen={false} />
                ) : (
                    <Card className="mb-0 w-100 border-0 box-shadow-none mb-0">
                        <Card.Header className="pb-0">
                            <div className="header-title">
                                <div className="d-flex justify-content-center flex-wrap gap-3">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Open Profile in {platform.name}</Tooltip>}
                                    >
                                        <Button
                                            className="sm social-btn bg-white rounded-circle p-0 no-pointer d-flex align-items-center justify-content-center"
                                            style={{ width: 40, height: 40 }}
                                            onClick={() => openLink(data.profile.url)}
                                        >
                                            <Image
                                                className="img-fluid"
                                                src={profileImage ? profileImage : logos[platform.name]}
                                                alt={data.profile.username}
                                                onError={() => setProfileImage(logos[platform.name])}
                                                style={{ width: '25px', height: '25px' }}
                                            />
                                        </Button>
                                    </OverlayTrigger>
                                    <div className="media-support-info">
                                        <div className="d-flex align-items-center mb-1 gap-2">
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{data.profile.name}</Tooltip>}
                                            >
                                                <h6 className="mb-0">{data.profile.username}</h6>
                                            </OverlayTrigger>
                                        </div>
                                        <p className="mb-0 small text-gray-200">
                                            <ReactTimeAgo date={new Date(data.postedAt)} locale="en-US" />
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <Dropdown>
                                <Dropdown.Toggle as={CustomToggle} className="">
                                    <span className="text-blue-300">
                                        <VerticalDotsIcon />
                                    </span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        className=" d-flex align-items-center text-blue-300"
                                        onClick={() => deleteConfirmation()}
                                    >
                                        <TrashIcon />
                                        <span className="ms-2">Delete Feed</span>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Card.Header>
                        <Card.Body>
                            <div className="user-post">
                                {data.title && <h5 className="mb-3">{data.title}</h5>}
                                {(media[0]?.type === 'image' || media[0]?.type === 'video') && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Open Post in {platform.name}</Tooltip>}
                                    >
                                        {media[0]?.type === 'image' || media[0].previewImageUrl ? (
                                            <Image
                                                src={
                                                    media[0]?.type === 'image' ? media[0].url : media[0].previewImageUrl
                                                }
                                                alt="post-image"
                                                style={{ width: '100%' }}
                                                className="img-fluid rounded"
                                                role="button"
                                                onClick={() => openLink(data.url)}
                                            />
                                        ) : (
                                            <video controls width="100%">
                                                <source src={media[0].url} type="video/mp4" />
                                            </video>
                                        )}
                                    </OverlayTrigger>
                                )}
                            </div>
                            <div className="comment-area pt-3">
                                <p className="small text-white mb-0">{data.text}</p>
                                {data.comments?.length > 0 && <Comments comments={data.comments} platform={platform} />}
                                <div className="py-3">
                                    <Sentiment feed={data} />
                                    {keywords.map((keyword, index) => (
                                        <Badge className="badge rounded-pill text-white mr-2" bg="primary" key={index}>
                                            {keyword}
                                        </Badge>
                                    ))}
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Button className="btn btn-secondary" onClick={() => openLink(data.url)}>
                                        Read Post
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
            <ToastContainer />
        </Fragment>
    )
}

export default FeedCard
